import { ApiService } from './api';
import { Customer, CreateCustomerData, UpdateCustomerData } from '@/models/customer';
import { ApiResponse } from '@/models/common';
import { getCachedCustomers, cacheCustomers, clearCustomersCache } from '@/utils/sessionManager';
import { ApiEndpoints, Endpoints } from '@/models/endpoints';

export class CustomerService {
  // Get all customers (no filters, returns total data)
  static async getCustomers(): Promise<ApiResponse<Customer[]>> {
    const response = await ApiService.get<Customer[]>(ApiEndpoints.CUSTOMERS);

    if (response.success && Array.isArray(response.data)) {
      return {
        ...response,
        data: response.data.filter(customer => customer.is_deleted !== 1)
      };
    }

    return response;
  }

  // Get customer by ID
  static async getCustomerById(id: string): Promise<ApiResponse<Customer>> {
    return ApiService.get<Customer>(Endpoints.getCustomer(id));
  }

  // Create new customer
  static async createCustomer(customerData: CreateCustomerData): Promise<ApiResponse<Customer>> {
    const response = await ApiService.post<Customer>(ApiEndpoints.CUSTOMERS, customerData);
    if (response.success) {
      // Clear cache when new customer is created
      clearCustomersCache();
    }
    return response;
  }

  // Update customer
  static async updateCustomer(id: string, customerData: UpdateCustomerData): Promise<ApiResponse<Customer>> {
    const response = await ApiService.put<Customer>(Endpoints.updateCustomer(id), customerData);
    if (response.success) {
      // Clear cache when customer is updated
      clearCustomersCache();
    }
    return response;
  }

  // Delete customer (soft delete by setting is_deleted to 1)
  static async deleteCustomer(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await ApiService.delete<{ message: string }>(Endpoints.deleteCustomer(id));
    if (response.success) {
      clearCustomersCache();
    }
    return response;
  }

  // Search customers by company name (client-side filtering)
  static async searchCustomers(query: string): Promise<ApiResponse<Customer[]>> {
    return ApiService.get<Customer[]>(Endpoints.searchCustomers(query));
  }

  // Get customers for dropdown/autocomplete (only non-deleted)
  static async getCustomersForSelect(): Promise<ApiResponse<Customer[]>> {
    // Check cache first
    const cachedCustomers = getCachedCustomers();
    if (cachedCustomers) {
      return {
        success: true,
        data: cachedCustomers as Customer[],
        message: 'Loaded from cache'
      };
    }

    // Use the standard getCustomers API and filter non-deleted customers
    const response = await ApiService.get<Customer[]>(ApiEndpoints.CUSTOMERS);
    
    if (response.success) {
      
      // Handle different response structures
      let customersData: Customer[];
      
      if (Array.isArray(response.data)) {
        // Direct array response
        customersData = response.data;
      } else {
        console.error('Unexpected response structure:', response.data);
        return {
          success: false,
          data: [],
          error: 'Unexpected response structure'
        };
      }
      
      // Filter out deleted customers
      const activeCustomers = customersData.filter(customer => customer.is_deleted === 0);
      
      // Cache the results
      cacheCustomers(activeCustomers);
      
      return {
        success: true,
        data: activeCustomers,
        message: 'Success'
      };
    }
    

    return {
      success: false,
      data: [],
      error: response.error || 'Failed to load customers'
    };
  }

  // Bulk delete customers (soft delete)
  static async bulkDeleteCustomers(ids: string[]): Promise<ApiResponse<{ message: string; deletedCount: number }>> {
    return ApiService.post<{ message: string; deletedCount: number }>(ApiEndpoints.CUSTOMERS_BULK_DELETE, { ids });
  }

  // Export customers to CSV
  static async exportCustomers(): Promise<Blob> {
    const response = await fetch(ApiEndpoints.CUSTOMERS_EXPORT, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to export customers');
    }
    
    return response.blob();
  }
} 